import { useEditor, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import { useCallback } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import useViewport from "src/hooks/use-viewport";
import getViewportValue from "src/utils/get-viewport-value";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import {
  ColorLabels,
  getColorByLabel,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import RegistrationWidget from "@/components/registration-widget/registration-widget-component";
import SimpleFlowRedirect from "@/components/simple-flow-redirect/simple-flow-redirect-component";
import useAnalytics from "@/hooks/use-analytics";
import { isAttendeesPage } from "@/utils/is-attendees-page";

import ActionSettings from "./action-component-settings";
import { StyledButton, StyledLink } from "./action-component-styles";

// Changing the values for `TAction`s will break support for legacy pages which
// use the persisted legacy values.  So when migrating to new values, ensure that
// you have added coverage for your new values in the `LegacyAction` type and
// `ACTION_VALUES_TO_CURRENT_ACTIONS` lookup table.
export type TAction =
  | "None"
  | "Ticket selector"
  | "Section of page"
  | "External link"
  | "Top of page"
  | "Bottom of page";

export type LegacyAction = "Registration page";

export const ACTIONS: { [k: string]: TAction } = {
  none: "None",
  registrationPage: "Ticket selector",
  sectionOfPage: "Section of page",
  externalLink: "External link",
  topOfPage: "Top of page",
  bottomOfPage: "Bottom of page",
};

type ActionValues = TAction | LegacyAction;

const ACTION_VALUES_TO_CURRENT_ACTIONS: { [key in ActionValues]: string } = {
  None: ACTIONS.none,
  "Ticket selector": ACTIONS.registrationPage,
  "Registration page": ACTIONS.registrationPage,
  "Section of page": ACTIONS.sectionOfPage,
  "External link": ACTIONS.externalLink,
  "Top of page": ACTIONS.topOfPage,
  "Bottom of page": ACTIONS.bottomOfPage,
};

const fullWidth = {
  width: "100%",
};

type HandlerAttrs = {
  href: string;
  onClick?: (event: MouseEvent) => void;
  target?: string;
};

export const getActionOrDefault = (props: ActionComponentProps) => {
  const action = props._action || props.action || ACTIONS.none;
  return ACTION_VALUES_TO_CURRENT_ACTIONS[action];
};

export const getHandlerAttrs = (
  enabled: boolean,
  handleRegistrationWidgetToggle: () => void,
  props: ActionComponentProps
): null | Record<any, never> | HandlerAttrs => {
  if (enabled) {
    return null;
  }

  const actionsToHandlerAttrs: Partial<Record<TAction, HandlerAttrs>> = {
    [ACTIONS.registrationPage]: {
      href: "#",
      onClick: (event: MouseEvent) => {
        event.preventDefault();
        handleRegistrationWidgetToggle();
      },
    },
    [ACTIONS.sectionOfPage]: {
      href: "#",
      onClick: (event: MouseEvent) => {
        event.preventDefault();
        const node = document.getElementById(props.scrollToSectionId);

        if (node) {
          scrollIntoView(node, {
            block: "start",
            behavior: "smooth",
          });
        }
      },
    },
    [ACTIONS.externalLink]:
      props.element === "button"
        ? {
            href: "#",
            onClick: (event: MouseEvent) => {
              event.preventDefault();
              window.open(props.externalLink, "_blank");
            },
          }
        : {
            href: props.externalLink,
            target: "_blank",
          },
    [ACTIONS.topOfPage]: {
      href: "#",
      onClick: (event: MouseEvent) => {
        event.preventDefault();
        window.scroll({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      },
    },
    [ACTIONS.bottomOfPage]: {
      href: "#",
      onClick: (event: MouseEvent) => {
        event.preventDefault();
        window.scrollTo({
          top:
            document.body.scrollHeight || document.documentElement.scrollHeight,
          left: 0,
          behavior: "smooth",
        });
      },
    },
  };

  const action = getActionOrDefault(props);

  return actionsToHandlerAttrs[action] || {};
};

export type ActionComponentStyle = Omit<React.CSSProperties, "padding"> & {
  padding?: [string | number, string | number, string | number];
};

type InternalActionComponentProps = {
  _text?: string;
  _inheritFontIndex?: number;
  _inheritColorIndex?: number;
  _action?: TAction;
};

export type ActionComponentProps = {
  /**
   * `wrapper` – use if you are rendering a list of Actions ex; "li"
   */
  wrapper?: keyof JSX.IntrinsicElements;
  element: "button" | "link";
  text?: string;
  action?: TAction;
  actions?: TAction[];
  externalLink?: string;
  scrollToSectionId?: string;
  style?: ActionComponentStyle;
  showOptions?: boolean;
  disableLinkToForRegistrationWidget?: boolean;
  /**
   * `inheritFontIndex` the index of type `Font` to use from the `FontProvider`
   */
  inheritFontIndex?: number;
  inheritColorIndex?: number;
  colorSet?: string[];
} & InternalActionComponentProps;

/**
 * @deprecated use Button editor/primitives/button-component
 */
export default function Action(props: ActionComponentProps) {
  const [isTicketSelectorOpen, setTicketSelectorOpen] = React.useState(false);
  const { logAnalytics } = useAnalytics();
  const {
    connectors: { connect },
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const handleRegistrationWidgetToggle = useCallback(() => {
    setTicketSelectorOpen((isOpen) => !isOpen);
    if (isAttendeesPage()) {
      logAnalytics("REGISTER_TICKET_CLICKED", {
        type: "canvas",
      });
    }
  }, [logAnalytics]);

  const viewport = useViewport();
  const primaryColor = getColorByLabel(ColorLabels.PRIMARY);
  const invertedFontColor = useInvertedColor(primaryColor);

  const style = React.useMemo(() => {
    return {
      ...props.style,
      fontSize: props.style?.fontSize || rem(15),
      fontWeight: props.style?.fontWeight || 600,
      color: props.style?.color || invertedFontColor,
      backgroundColor: props.style?.backgroundColor || primaryColor,
      padding: props.style?.padding
        ? getViewportValue(props.style?.padding, viewport)
        : 0,
      position: "relative",
      ...(props.colorSet && {
        backgroundColor: props.colorSet[0],
        color: props.colorSet[1],
        outline: `0.125rem solid ${props.colorSet[2]}`,
      }),
    };
  }, [invertedFontColor, primaryColor, props.style, props.colorSet, viewport]);

  const handlerAttrs = React.useMemo(
    () =>
      getHandlerAttrs(enabled, handleRegistrationWidgetToggle, {
        action: props.action,
        _action: props._action,
        scrollToSectionId: props.scrollToSectionId,
        element: props.element,
        externalLink: props.externalLink,
      }),
    [
      enabled,
      props.action,
      props._action,
      props.scrollToSectionId,
      props.element,
      props.externalLink,
      handleRegistrationWidgetToggle,
    ]
  );

  const Wrapper = props.wrapper || React.Fragment;

  const Comp: React.ElementType =
    props.element === "button" ? StyledButton : StyledLink;

  // Don't render anything if user explicitly deletes all text
  if (props.text?.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <Wrapper>
        <div ref={connect} style={fullWidth}>
          <Comp style={style} {...handlerAttrs} data-testid="action-component">
            {typeof props._text === "undefined" ? props.text : props._text}
          </Comp>
        </div>
      </Wrapper>
      {getActionOrDefault(props) === ACTIONS.registrationPage && (
        <SimpleFlowRedirect
          isOpen={isTicketSelectorOpen}
          onClose={() => setTicketSelectorOpen(false)}
          renderWidget={(isShowing, handleClose) => (
            <RegistrationWidget isOpen={isShowing} onClose={handleClose} />
          )}
        />
      )}
    </React.Fragment>
  );
}

Action.craft = {
  custom: {
    displayName: "Action",
  },
  related: {
    settings: ActionSettings,
    leftOutlineOptions: PrimitivesFloatingOptions,
  },
};
